import React from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import styled, { createGlobalStyle } from "styled-components";

import {
  getAvailableColors,
  hasInsertVariants,
  getThumbCategory,
  hasThumbVariants,
  getThumbSimpleColors,
  getThumbSwitchInnerColors,
  getThumbSwitchOuterColors,
  getThumbSwitchInnerSizes,
} from "../db/datafixtures/insert_variants_helpers";

const PickerStyle = createGlobalStyle`
  .insert-color-picker .modal-content {
    border: none;
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow:
      inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }
  .insert-color-picker .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }
  .insert-color-picker .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    overflow: visible;
  }
  .insert-color-picker .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
  .insert-color-picker .ant-select-selector {
    border-radius: ${(p) => p.theme.radius.sm} !important;
    border-color: ${(p) => p.theme.color.border} !important;
  }
  .insert-color-picker .ant-select-focused .ant-select-selector {
    border-color: ${(p) => p.theme.color.primary} !important;
    box-shadow: 0 0 0 2px rgba(0, 24, 126, 0.15) !important;
  }
  .insert-color-picker-dropdown .ant-select-item-option-selected {
    background: ${(p) => p.theme.color.surfaceAlt} !important;
    font-weight: ${(p) => p.theme.font.weightSemibold} !important;
  }
`;

const COLOR_HEX = {
  BLACK: "#1A1A1A",
  WHITE: "#F5F5F5",
  RED: "#D32F2F",
  BLUE: "#1976D2",
  GREEN: "#2E7D32",
  ORANGE: "#F57C00",
  PURPLE: "#7B1FA2",
  PINK: "#EC407A",
  YELLOW: "#FBC02D",
  ICE: "#B3E5FC",
  CERISE: "#C2185B",
  GREY: "#9E9E9E",
  GRAY: "#9E9E9E",
};

const Title = styled.span`
  color: ${(p) => p.theme.color.primary};
  font-family: ${(p) => p.theme.font.family};
  font-weight: ${(p) => p.theme.font.weightBold};
  font-size: ${(p) => p.theme.font.sizeLg};
`;

const InfoCard = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};
  text-align: center;
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const Triangle = styled.div`
  display: grid;
  justify-items: center;
  row-gap: ${(p) => p.theme.spacing.md};
  margin: ${(p) => p.theme.spacing.md} 0;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: ${(p) => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const Slot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(p) => p.theme.spacing.xs};
  min-width: 140px;
`;

const Circle = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 2px solid ${(p) => p.theme.color.primary};
  background: ${(p) => p.theme.color.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  font-family: ${(p) => p.theme.font.family};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.1;
`;

const SlotLabel = styled.div`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Empty = styled.div`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  text-align: center;
  padding: ${(p) => p.theme.spacing.md} 0;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
`;

const Swatch = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(p) => p.$bg};
  border: 1px solid rgba(0, 0, 0, 0.18);
  flex-shrink: 0;
`;

const titleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const swatchFor = (color) => COLOR_HEX[String(color).toUpperCase()] || "#CFD2DA";

const ColorSelect = ({ colors, value, onChange, placeholder }) => {
  const options = colors.map((c) => ({
    value: c,
    label: (
      <OptionRow>
        <Swatch $bg={swatchFor(c)} />
        <span>{titleCase(c)}</span>
      </OptionRow>
    ),
    plainLabel: titleCase(c),
  }));
  return (
    <Select
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      placeholder={placeholder}
      options={options}
      allowClear
      style={{ width: "100%", minWidth: 140 }}
      popupClassName="insert-color-picker-dropdown"
      optionLabelProp="label"
      getPopupContainer={(trigger) => trigger.parentElement}
      dropdownStyle={{ zIndex: 1080 }}
    />
  );
};

const SubLabel = styled.div`
  font-family: ${(p) => p.theme.font.family};
  font-size: 10px;
  color: ${(p) => p.theme.color.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: ${(p) => p.theme.spacing.xs};
`;

const InsertSlot = ({ slotKey, label, typeId, size, value, onChange }) => {
  const { t } = useTranslation();
  const colors = React.useMemo(
    () => getAvailableColors(typeId, size),
    [typeId, size],
  );
  if (!typeId || !size || colors.length === 0) return null;
  return (
    <Slot>
      <SlotLabel>{label}</SlotLabel>
      <Circle>{size}</Circle>
      <ColorSelect
        colors={colors}
        value={value}
        onChange={(v) => onChange(slotKey, v)}
        placeholder={t("spectrepos.color_select_placeholder")}
      />
    </Slot>
  );
};

const ThumbSlot = ({ label, typeId, size, value, onChange }) => {
  const { t } = useTranslation();
  const category = React.useMemo(() => getThumbCategory(typeId), [typeId]);
  if (!typeId || !category) return null;

  if (category === "simple") {
    const colors = getThumbSimpleColors(typeId, size);
    if (!size || colors.length === 0) return null;
    return (
      <Slot>
        <SlotLabel>{label}</SlotLabel>
        <Circle>{size}</Circle>
        <ColorSelect
          colors={colors}
          value={value?.slug}
          onChange={(v) =>
            onChange({ category: "simple", typeId, size, slug: v })
          }
          placeholder={t("spectrepos.color_select_placeholder")}
        />
      </Slot>
    );
  }

  // Switch: pick an inner size that matches SpecSheet's thm_size if it's
  // in the fixture, else fall back to the first available size.
  const availableInnerSizes = getThumbSwitchInnerSizes(typeId);
  if (availableInnerSizes.length === 0) return null;
  const innerSize = availableInnerSizes.includes(String(size))
    ? String(size)
    : availableInnerSizes[0];
  const innerColors = getThumbSwitchInnerColors(typeId, innerSize);
  const outerColors = getThumbSwitchOuterColors(typeId);
  if (innerColors.length === 0 && outerColors.length === 0) return null;
  return (
    <Slot style={{ minWidth: 240 }}>
      <SlotLabel>{label}</SlotLabel>
      <Circle>{innerSize}</Circle>
      {innerColors.length > 0 && (
        <>
          <ColorSelect
            colors={innerColors}
            value={value?.slug}
            onChange={(v) =>
              onChange({
                category: "switch",
                typeId,
                inner_size: innerSize,
                slug: v,
                outer: value?.outer || null,
              })
            }
            placeholder={t("spectrepos.thumb_slug_placeholder")}
          />
          <SubLabel>{t("spectrepos.thumb_slug")}</SubLabel>
        </>
      )}
      {outerColors.length > 0 && (
        <>
          <ColorSelect
            colors={outerColors}
            value={value?.outer}
            onChange={(v) =>
              onChange({
                category: "switch",
                typeId,
                inner_size: innerSize,
                slug: value?.slug || null,
                outer: v,
              })
            }
            placeholder={t("spectrepos.thumb_outer_placeholder")}
          />
          <SubLabel>{t("spectrepos.thumb_outer")}</SubLabel>
        </>
      )}
    </Slot>
  );
};

const InsertColorPicker = ({
  show,
  onHide,
  onConfirm,
  specSheet,
  is2Hander,
  initialColors,
}) => {
  const { t } = useTranslation();
  const isLeft = specSheet?.hand === "left_hander";
  const f1TypeId = isLeft ? specSheet?.f2_grip_type : specSheet?.f1_grip_type;
  const f2TypeId = isLeft ? specSheet?.f1_grip_type : specSheet?.f2_grip_type;
  const f1Size = isLeft ? specSheet?.f2_size : specSheet?.f1_size;
  const f2Size = isLeft ? specSheet?.f1_size : specSheet?.f2_size;
  const thmTypeId = is2Hander ? null : specSheet?.thm_insert_type;
  const thmSize = is2Hander ? null : specSheet?.thm_size;

  const f1Available =
    f1TypeId && f1Size && getAvailableColors(f1TypeId, f1Size).length > 0;
  const f2Available =
    f2TypeId && f2Size && getAvailableColors(f2TypeId, f2Size).length > 0;
  const thmAvailable = thmTypeId && hasThumbVariants(thmTypeId);
  const thmCategory = thmAvailable ? getThumbCategory(thmTypeId) : null;

  const anyAvailable = f1Available || f2Available || thmAvailable;

  const [colors, setColors] = React.useState(initialColors || {});
  React.useEffect(() => {
    if (show) setColors(initialColors || {});
  }, [show, initialColors]);

  const setColor = (slot, value) =>
    setColors((prev) => ({ ...prev, [slot]: value }));

  const handleConfirm = () => {
    const out = {};
    const slots = {
      f1: f1Available ? { typeId: f1TypeId, size: f1Size } : null,
      f2: f2Available ? { typeId: f2TypeId, size: f2Size } : null,
      thm: null,
    };
    if (f1Available && colors.f1) out.f1 = colors.f1;
    if (f2Available && colors.f2) out.f2 = colors.f2;
    if (thmAvailable && colors.thm) {
      out.thm = colors.thm; // object shape: {category, typeId, size?, inner_size?, slug, outer?}
      slots.thm = {
        typeId: thmTypeId,
        size: thmSize,
        category: thmCategory,
        inner_size: colors.thm?.inner_size,
      };
    }
    onConfirm({ colors: out, slots });
  };

  return (
    <>
      <PickerStyle />
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="insert-color-picker"
      >
        <Modal.Header closeButton>
          <Title>{t("spectrepos.insert_picker_title")}</Title>
        </Modal.Header>
        <Modal.Body>
          <InfoCard>{t("spectrepos.insert_picker_body")}</InfoCard>
          {!anyAvailable ? (
            <Empty>{t("spectrepos.insert_picker_none")}</Empty>
          ) : (
            <Triangle>
              <Row>
                <InsertSlot
                  slotKey="f1"
                  label={t("spectrepos.finger_1")}
                  typeId={f1TypeId}
                  size={f1Size}
                  value={colors.f1}
                  onChange={setColor}
                />
                <InsertSlot
                  slotKey="f2"
                  label={t("spectrepos.finger_2")}
                  typeId={f2TypeId}
                  size={f2Size}
                  value={colors.f2}
                  onChange={setColor}
                />
              </Row>
              {thmAvailable && (
                <Row>
                  <ThumbSlot
                    label={t("spectrepos.thumb")}
                    typeId={thmTypeId}
                    size={thmSize}
                    value={colors.thm}
                    onChange={(v) => setColor("thm", v)}
                  />
                </Row>
              )}
            </Triangle>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>
            {t("message.confirm.cancel")}
          </Button>
          <Button variant="outline-primary" onClick={handleConfirm}>
            {t("spectrepos.insert_picker_confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const hasAnyInsertVariants = (specSheet, is2Hander) => {
  if (!specSheet) return false;
  const isLeft = specSheet?.hand === "left_hander";
  const f1 = isLeft ? specSheet?.f2_grip_type : specSheet?.f1_grip_type;
  const f2 = isLeft ? specSheet?.f1_grip_type : specSheet?.f2_grip_type;
  const thm = is2Hander ? null : specSheet?.thm_insert_type;
  return (
    hasInsertVariants(f1) ||
    hasInsertVariants(f2) ||
    hasThumbVariants(thm)
  );
};

export default InsertColorPicker;
